/* eslint-disable @typescript-eslint/dot-notation */
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { faUnlock, faUnlockAlt } from '@fortawesome/free-solid-svg-icons';

import { EditUserVm } from 'app/shared/generated/Administration/Models/User/EditUser/EditUserVm';
import { SystemMessageService, SystemMessage } from 'app/core/system-message/system-message-service';
import { NavigationService } from 'app/shared/navigation/navigation.service';
import { SecurityService } from 'app/core/security/security.service';
import { UserChangePasswordModalComponent } from './user-change-password-modal/user-change-password-modal.component';
import { ActivationService } from 'app/shared/services/activation.service';
import { ActivationEnum } from 'app/core/enums/generated/ActivationEnum';
import { UserPermissionsModalComponent } from './user-permissions-modal/user-permissions-modal.component';

@Component({
    selector: 'pcg-edit-user',
    templateUrl: './edit-user.component.html',
    styleUrls: ['./edit-user.component.scss'],
    standalone: false
})
export class EditUserComponent implements OnInit, OnDestroy {

	id: number;
	isLockedOut = false;
	subscriptions = new Subscription();
	formGroup = EditUserVm.Form;
	model = EditUserVm.GetModel(this.formGroup);
	faIconName = { faUnlock, faUnlockAlt };
	isAdmin = false;

	userNameValidation = {
		pattern: "Only letters, numbers, and symbols ( . @ - _ ' ) are allowed",
	};

	fullNameValidation = {
		pattern: "Only letters, dashes ( - ), and single quotes ( ' ) are allowed",
	};

	// Security variables
	canManageUsers = false;
	canChangePermissions = false;
	canSeePasswordButtons = false;
	isCurrentUser = false;
	
	viewModelString = 'Administration.Models.User.EditUser.EditUserVm';
    auditHistoryName = 'Edit User Audit History';
	activeTab: string;

	constructor(private route: ActivatedRoute, private httpClient: HttpClient, public router: Router
		, private modalService: NgbModal, private ms: SystemMessageService, private nav: NavigationService
		, title: Title, private sec: SecurityService, private activationService: ActivationService) {
		title.setTitle('Edit User');
	}

	openWindow = (o: string) => { this.router.navigateByUrl('/' + o); };	

	ngOnInit() {
		this.addSecuritySubscription();
		this.addRouteSubscription();
	}

	/** Update the local dynamic security object when the security updates */
	addSecuritySubscription() {
		this.subscriptions.add(
			this.sec.security$.subscribe(() => {
				this.canManageUsers = this.sec.hasAccess(o => o.manageUsers);
				this.canChangePermissions = this.sec.hasAccess(o => o.canChangePermissions);
				this.sec.setControlAccess(this.formGroup.controls['permissionId'], o => o.canChangePermissions);
			})
		);
	}

	/** Subscribe to route to pick up id changes and update page accordingly */
	addRouteSubscription() {
		this.subscriptions.add(
			this.route.paramMap.subscribe(map => {
				// Allow user to view page if it is either their profile
				// or they have admin access to view other people's profiles
				const user = this.sec.getUser();
				this.id = parseInt(map.get('id'), 10);
				if (!this.canManageUsers && this.id !== user.id) { this.router.navigateByUrl('dashboard'); }
				this.isCurrentUser = this.id === this.sec.getUser().id;
				this.subscriptions.add(
					this.httpClient
						.get(`api/Administration/Users/GetUser/${this.id}`)
						.subscribe((model: EditUserVm) => {
							this.isAdmin = user.isAdmin;
							this.formGroup.patchValue(model);
							this.canSeePasswordButtons =
								model.isActive &&
								!model.isLockedOut &&
								model.userId !== this.sec.getUser().id &&
								model.userId !== 0;
							this.isLockedOut = model.isLockedOut;
						})
				);

				// I'm both setting the secondary nav in ngOnInit and on nav change
				// since the secondary nav was otherwise not changing when the username
				// in the corner was clicked while on this page
				this.setSecondaryNav();
				this.subscriptions.add(
					this.router.events
						.pipe(filter(event => event instanceof NavigationEnd))
						.subscribe(() => { this.setSecondaryNav(); })
				);
			})
		);
	}

	/** Set the navigation that sits under the main navigation */
	setSecondaryNav() { this.nav.setOpenSecondaryMenu(['Admin', 'Users', 'User List']); }

	/** The main save event for the page */
	saveUser(msg: SystemMessage) {
		if (!msg.value) { return; }
		const model = this.formGroup.value;
		this.id = model.userId;
		this.canSeePasswordButtons = model.isActive && !model.isLockedOut && model.userId !== this.sec.getUser().id && model.userId !== 0;
		this.sec.setSecurity(undefined, msg.value.userJsVm, msg.value.dynamicSecurity);
		if (msg.isSuccessful) {
			this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
				this.router.navigate([`/edit-user/${this.id}`]);
			});		
		}
	}

	/** Unlock user, called when the unlock svg clicked on locked user */
	unlockUser() {
		this.subscriptions.add(
			this.ms.getHttpObservable(this, `api/Administration/Users/UnlockUser/${this.id}`).subscribe(() => {
				this.isLockedOut = false;
			})
		);
	}

	/** Called on click of the reset password and welcome email buttons */
	resetPassword(isWelcomeEmail: boolean) {
		this.subscriptions.add(
			this.httpClient
				.post('api/Account/ForgotPassword', {
					email: this.formGroup.value.email,
					isWelcomeEmail,
				}).subscribe((msg: SystemMessage) => {
					if (msg.isSuccessful) {
						this.ms.setSystemMessage(
							`An email has been sent to the user with a ${
								isWelcomeEmail ? 'welcome' : 'reset password'
							} link.`
						);
					} else {
						this.ms.setSystemMessage('An error occurred when attempting to send the email.', 'error');
					}
				})
		);
	}

	saveUsername() {
		if (this.id === 0) { return; }
		this.subscriptions.add(
            this.ms.getHttpObservable(this, 'api/Administration/Users/SaveUser', this.formGroup).subscribe()
        );
	}

	changePermissions() {
		if (this.id === 0) { return; }
		this.subscriptions.add(
            this.ms.getHttpObservable(this, 'api/Administration/Users/SaveUser', this.formGroup).subscribe()
        );
	}

	/** Opens the change password modal.
	 * Called on click of the Change Passoword button when user looking at their profile. */
	openChangePasswordModal() { this.modalService.open(UserChangePasswordModalComponent); }

	openPermissionsInfoModal() {
		this.modalService.open(UserPermissionsModalComponent, {
			size: 'xl',
		});
	}

	activateDeactivate() {
		this.activationService.activateDeactivate(this.id, ActivationEnum.User, this.formGroup.value.isActive ? false : true);
	}

	ngOnDestroy() { this.subscriptions.unsubscribe(); }
}
